import { Crown, Loader2 } from 'lucide-react';
import {
  membershipPlansFromQueryData,
  useMembershipPlans,
} from '@/hooks/pricebook/useMembershipPlans';
import type { ItemType } from '@/types/pricebook';
import { fmt } from './support/pricebook-helpers';
import { buildMembershipPricingBreakdown } from './support/membership-pricing-breakdown';

/**
 * Sidebar/aside block that shows "what this item will cost to members of each
 * active membership plan", e.g. `Base $100 → Gold (20% off) → $80`.
 *
 * Render rules (NOT-54):
 *   - Loading state shows a small spinner row rather than collapsing silently.
 *   - When the tenant has no active membership plans → render an "empty" hint
 *     so the user understands the section is intentionally empty.
 *   - When `basePrice` is missing/zero, still render every eligible plan row
 *     but show "—" for the final price; never disappear silently.
 *   - For membership items there is no breakdown to show (the price is
 *     defined elsewhere) so the caller should skip mounting this component.
 */
export function MembershipPricingBreakdown({
  basePrice,
  itemType,
  itemId,
  memberPrice,
  memberDiscountPercent,
}: {
  basePrice: number | null | undefined;
  itemType: ItemType;
  itemId?: string;
  /** Per-item absolute member price — wins over any plan discount when set. */
  memberPrice?: number | null;
  /** Per-item member discount % — final fallback when the plan gives nothing. */
  memberDiscountPercent?: number | null;
}) {
  const query = useMembershipPlans({ status: 'ACTIVE' }, { showErrorToast: false });
  const plans = membershipPlansFromQueryData(query.data);

  if (query.isLoading) {
    return (
      <div
        className="flex items-center gap-1.5 text-xs text-muted-foreground"
        data-testid="membership-breakdown-loading"
      >
        <Loader2 size={12} className="animate-spin" />
        Loading membership pricing…
      </div>
    );
  }

  if (query.isError) {
    return (
      <p className="text-xs text-muted-foreground" data-testid="membership-breakdown-error">
        Membership pricing unavailable.
      </p>
    );
  }

  const rows = buildMembershipPricingBreakdown({
    basePrice,
    itemType,
    itemId,
    plans,
    memberPrice,
    memberDiscountPercent,
  });

  if (rows.length === 0) {
    return (
      <p className="text-xs text-muted-foreground" data-testid="membership-breakdown-empty">
        {plans.length === 0
          ? 'No active membership plans configured.'
          : 'No membership plans apply to this item type.'}
      </p>
    );
  }

  return (
    <div className="space-y-1.5" data-testid="membership-breakdown">
      {rows.map((row) => (
        <div key={row.planId || row.planName} className="flex items-start justify-between gap-3">
          <span className="inline-flex min-w-0 items-center gap-1.5 text-muted-foreground">
            <Crown size={11} className="shrink-0 text-amber-500" aria-hidden="true" />
            <span className="truncate">{row.planName}</span>
            <span className="shrink-0 rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
              {row.discountLabel}
            </span>
          </span>
          <span className="shrink-0 tabular-nums" title={row.tierLabel ?? undefined}>
            {row.finalPrice == null ? '—' : fmt(row.finalPrice)}
          </span>
        </div>
      ))}
    </div>
  );
}
